<template>
  <div class="hms-page">
    <Navigation />

    <div class="container hms-page-body" v-if="cfg">
      <!-- Header -->
      <div class="d-flex align-items-center justify-content-between mb-4 hms-page-header">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.push('/registry')" class="hms-back" title="Back to registries">
            <i class="mdi mdi-arrow-left"></i>
          </a>
          <div class="hms-reg-title-pill" :style="'background:' + cfg.color + '20; color:' + cfg.color">
            <i :class="cfg.icon"></i>
          </div>
          <h4 class="hms-page-title m-0">{{ cfg.title }}</h4>
        </div>
        <div class="d-flex gap-2">
          <button class="hms-btn hms-btn-primary" @click="openEnroll">
            <i class="mdi mdi-account-plus me-1"></i>Enroll Patient
          </button>
        </div>
      </div>

      <!-- Stats -->
      <div class="hms-stat-row">
        <div v-for="opt in cfg.statusOptions" :key="opt.value" class="hms-stat" @click="filterStatus = opt.value" :class="{ selected: filterStatus === opt.value }">
          <div class="hms-stat-num" :class="opt.class.replace('hms-badge-','hms-text-c-')">{{ countByStatus(opt.value) }}</div>
          <div class="hms-stat-lbl">{{ opt.label }}</div>
        </div>
        <div class="hms-stat" @click="filterStatus = ''" :class="{ selected: filterStatus === '' }">
          <div class="hms-stat-num">{{ rows.length }}</div>
          <div class="hms-stat-lbl">All</div>
        </div>
      </div>

      <!-- List card -->
      <div v-if="loading" class="hms-spinner"><div class="spinner-border"></div></div>
      <div v-else class="hms-card">
        <div class="d-flex flex-wrap gap-2 mb-3 align-items-center">
          <div class="hms-search-wrap">
            <i class="mdi mdi-magnify"></i>
            <input class="hms-search" v-model="search" :placeholder="'Search ' + cfg.title.toLowerCase() + ' patients...'" />
          </div>
          <span class="hms-muted ms-auto">{{ filtered.length }} patients</span>
        </div>

        <div v-if="filtered.length" style="overflow-x:auto;">
          <table class="hms-table">
            <thead>
              <tr>
                <th v-for="col in cfg.columns" :key="col.key">{{ col.label }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in filtered" :key="row[cfg.idField]">
                <td v-for="col in cfg.columns" :key="col.key">
                  <template v-if="col.type === 'patient'">
                    <div class="hms-text fw-semibold">{{ row.patient_name }}</div>
                    <div class="hms-muted-sm">{{ row.patient_no }}</div>
                  </template>
                  <template v-else-if="col.type === 'mono'">
                    <span class="hms-mono">{{ row[col.key] || '—' }}</span>
                  </template>
                  <template v-else-if="col.type === 'date'">
                    {{ formatDate(row[col.key]) }}
                  </template>
                  <template v-else-if="col.type === 'yesno'">
                    <span v-if="row[col.key] == 1" class="hms-badge hms-badge-warning">Yes</span>
                    <span v-else class="hms-muted-sm">No</span>
                  </template>
                  <template v-else-if="col.type === 'status'">
                    <span class="hms-badge" :class="statusClass(row[col.key])">{{ statusLabel(row[col.key]) }}</span>
                  </template>
                  <template v-else>
                    {{ row[col.key] != null && row[col.key] !== '' ? row[col.key] : '—' }}
                  </template>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="hms-empty">
          <i :class="cfg.icon" :style="'color:' + cfg.color + '60'"></i>
          <div v-if="search">No patients matched "{{ search }}".</div>
          <div v-else>No patients enrolled yet — click <strong>Enroll Patient</strong>.</div>
        </div>
      </div>

      <!-- ENROLL MODAL -->
      <div class="hms-modal-overlay" v-if="showEnroll" @click.self="showEnroll=false">
        <div class="hms-modal">
          <div class="hms-modal-title">
            <span><i :class="cfg.icon" class="me-2" :style="'color:' + cfg.color"></i>Enroll to {{ cfg.title }}</span>
            <button class="hms-modal-close" @click="showEnroll=false">&times;</button>
          </div>

          <div v-if="enrollError" class="hms-alert hms-alert-danger"><i class="mdi mdi-alert-circle me-1"></i>{{ enrollError }}</div>

          <!-- Patient picker -->
          <div class="mb-3">
            <label class="hms-label">Patient *</label>
            <div class="hms-search-wrap">
              <i class="mdi mdi-magnify"></i>
              <input class="hms-search" v-model="patientSearch" placeholder="Search name, patient no..." @input="onPatientSearch" />
            </div>
            <div v-if="patientResults.length" class="hms-suggest">
              <div v-for="pp in patientResults" :key="pp.patient_id" class="hms-suggest-item" @click="pickPatient(pp)">
                <div class="hms-text fw-semibold">{{ pp.first_name }} {{ pp.last_name }}</div>
                <div class="hms-muted-sm">{{ pp.patient_no }} · {{ pp.phone || '—' }}</div>
              </div>
            </div>
            <div v-if="enrollForm.patient_id" class="hms-picked mt-2">
              <i class="mdi mdi-check-circle me-1"></i>{{ enrollForm.patient_name }} ({{ enrollForm.patient_no }})
              <button class="hms-modal-close ms-auto" @click="clearPatient">&times;</button>
            </div>
          </div>

          <!-- Dynamic fields from config -->
          <div class="row g-3 mb-3">
            <div v-for="f in cfg.enrollFields" :key="f.name" :class="['col-md-6']">
              <label class="hms-label">{{ f.label }}</label>
              <input v-if="f.type === 'text' || f.type === 'number' || f.type === 'date'"
                     :type="f.type"
                     class="hms-input"
                     v-model="enrollForm[f.name]" />
              <select v-else-if="f.type === 'select'" class="hms-input" v-model="enrollForm[f.name]">
                <option v-for="o in f.options" :key="o.value" :value="o.value">{{ o.label }}</option>
              </select>
              <label v-else-if="f.type === 'checkbox'" class="hms-check">
                <input type="checkbox" v-model="enrollForm[f.name]" :true-value="1" :false-value="0" />
                Yes
              </label>
            </div>
          </div>

          <button class="hms-btn hms-btn-primary w-100" @click="doEnroll" :disabled="processing || !enrollForm.patient_id">
            <span v-if="processing" class="spinner-border spinner-border-sm me-1"></span>
            {{ processing ? 'Enrolling...' : 'Enroll' }}
          </button>
        </div>
      </div>

      <!-- Toast -->
      <transition name="hms-toast">
        <div v-if="toastMsg" class="hms-toast" :class="{ 'hms-toast-error': toastError }">
          <i class="mdi me-2" :class="toastError ? 'mdi-alert-circle' : 'mdi-check-circle'"></i>{{ toastMsg }}
        </div>
      </transition>
    </div>

    <!-- Unknown registry -->
    <div v-else class="container hms-page-body">
      <div class="hms-card hms-empty">
        <i class="mdi mdi-help-circle-outline"></i>
        Unknown registry type: {{ $route.params.type }}
        <button class="hms-btn hms-btn-blue mt-3" @click="$router.push('/registry')">Back to registries</button>
      </div>
    </div>
  </div>
</template>

<style>
:root,
html[data-theme="dark"] {
  --hms-h:#f0f0f0;--hms-s:#a0a0a0;--hms-m:#808080;
  --hms-bg1:#141414;--hms-bg2:#0a0a0a;--hms-bg3:#1e1e1e;--hms-bg4:#0f0f0f;
  --hms-br:#2a2a2a;--hms-input-br:#333333;--hms-placeholder:#4a4a4a;
  --hms-primary:#1a7fe8;--hms-primary-lt:rgba(26,127,232,0.12);
  --hms-accent:#F5821F;--hms-success:#28a745;--hms-danger:#dc3545;--hms-warning:#ffc107;--hms-info:#17a2b8;
}
html[data-theme="light"] {
  --hms-h:#1a1a1a;--hms-s:#555555;--hms-m:#777777;
  --hms-bg1:#ffffff;--hms-bg2:#f4f6f9;--hms-bg3:#eef1f5;--hms-bg4:#ffffff;
  --hms-br:#dee2e8;--hms-input-br:#ced4da;--hms-placeholder:#aaaaaa;
  --hms-primary:#0047AB;--hms-primary-lt:rgba(0,71,171,0.08);
  --hms-accent:#F5821F;--hms-success:#28a745;--hms-danger:#dc3545;--hms-warning:#ffc107;--hms-info:#17a2b8;
}
html[data-theme="light"] body, html[data-theme="dark"] body { background: var(--hms-bg2) !important; color: var(--hms-h) !important; }
</style>

<style scoped>
.hms-page { min-height: 100vh; background: var(--hms-bg2); padding-top: 70px; color: var(--hms-h); }
.hms-page-body { padding-top: 20px; padding-bottom: 40px; }
.hms-page-header { flex-wrap: wrap; gap: 12px; }
.hms-back { color: var(--hms-s); font-size: 22px; text-decoration: none; padding: 4px; }
.hms-back:hover { color: var(--hms-primary); }
.hms-page-title { color: var(--hms-h); font-size: 20px; font-weight: 700; }
.hms-reg-title-pill { width: 36px; height: 36px; border-radius: 10px; display: flex; align-items: center; justify-content: center; font-size: 18px; }

.hms-muted { color: var(--hms-s); font-size: 13px; }
.hms-muted-sm { color: var(--hms-s); font-size: 12px; }
.hms-text { color: var(--hms-h); }
.hms-mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 12px; color: var(--hms-h); }

.hms-stat-row { display: flex; flex-wrap: wrap; gap: 12px; margin-bottom: 20px; }
.hms-stat { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 12px; padding: 12px 16px; flex: 1; min-width: 130px; cursor: pointer; transition: all .15s; }
.hms-stat:hover { border-color: var(--hms-primary); transform: translateY(-2px); }
.hms-stat.selected { border-color: var(--hms-primary); background: var(--hms-primary-lt); }
.hms-stat-num { font-size: 22px; font-weight: 800; color: var(--hms-primary); line-height: 1.1; }
.hms-stat-lbl { font-size: 12px; color: var(--hms-s); margin-top: 4px; }
.hms-text-c-success { color: var(--hms-success); }
.hms-text-c-warning { color: var(--hms-warning); }
.hms-text-c-danger  { color: var(--hms-danger); }
.hms-text-c-info    { color: var(--hms-primary); }
.hms-text-c-neutral { color: var(--hms-m); }
.hms-text-c-purple  { color: #9b59b6; }

.hms-card { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 14px; padding: 20px; margin-bottom: 16px; }
.hms-spinner { display: flex; justify-content: center; padding: 40px; }
.hms-spinner .spinner-border { color: var(--hms-primary); }
.hms-empty { text-align: center; padding: 40px 20px; color: var(--hms-s); }
.hms-empty i { font-size: 48px; display: block; margin-bottom: 12px; }

.hms-search-wrap { position: relative; flex: 1; min-width: 240px; max-width: 360px; }
.hms-search-wrap i { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: var(--hms-s); }
.hms-search { width: 100%; background: var(--hms-bg4); border: 1px solid var(--hms-input-br); border-radius: 8px; padding: 8px 14px 8px 36px; color: var(--hms-h); font-size: 14px; outline: none; }
.hms-search:focus { border-color: var(--hms-primary); }

.hms-input { width: 100%; background: var(--hms-bg4); border: 1px solid var(--hms-input-br); border-radius: 8px; padding: 8px 14px; color: var(--hms-h); font-size: 14px; outline: none; }
.hms-input:focus { border-color: var(--hms-primary); }
.hms-label { color: var(--hms-m); font-size: 12px; font-weight: 600; margin-bottom: 4px; display: block; }
.hms-check { display: inline-flex; align-items: center; gap: 6px; color: var(--hms-h); font-size: 13px; cursor: pointer; padding-top: 6px; }
.hms-check input { accent-color: var(--hms-primary); }

.hms-table { width: 100%; border-collapse: collapse; }
.hms-table th { color: var(--hms-s); font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; padding: 10px 12px; border-bottom: 1px solid var(--hms-br); text-align: left; white-space: nowrap; }
.hms-table td { color: var(--hms-h); font-size: 13px; padding: 10px 12px; border-bottom: 1px solid var(--hms-br); vertical-align: middle; }
.hms-table tr:hover td { background: var(--hms-primary-lt); }

.hms-badge { padding: 3px 10px; border-radius: 50px; font-size: 11px; font-weight: 600; text-transform: capitalize; display: inline-block; }
.hms-badge-success { background: rgba(40,167,69,.15); color: var(--hms-success); }
.hms-badge-warning { background: rgba(255,193,7,.15); color: var(--hms-warning); }
.hms-badge-danger  { background: rgba(220,53,69,.15);  color: var(--hms-danger); }
.hms-badge-info    { background: var(--hms-primary-lt); color: var(--hms-primary); }
.hms-badge-neutral { background: var(--hms-bg3); color: var(--hms-m); }
.hms-badge-purple  { background: rgba(155,89,182,.15); color: #9b59b6; }

.hms-btn { border: none; border-radius: 8px; padding: 8px 16px; font-size: 13px; font-weight: 600; cursor: pointer; transition: all .15s; display: inline-flex; align-items: center; justify-content: center; }
.hms-btn:disabled { opacity: 0.6; cursor: not-allowed; }
.hms-btn-primary { background: var(--hms-primary); color: #fff; }
.hms-btn-primary:hover:not(:disabled) { filter: brightness(1.1); }
.hms-btn-blue { background: var(--hms-primary-lt); color: var(--hms-primary); border: 1px solid var(--hms-primary-lt); }
.hms-btn-blue:hover:not(:disabled) { background: var(--hms-primary); color: #fff; }

.hms-modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.6); z-index: 200; display: flex; align-items: flex-start; justify-content: center; padding: 40px 20px; overflow-y: auto; }
.hms-modal { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 16px; padding: 24px; width: 100%; max-width: 720px; max-height: calc(100vh - 80px); overflow-y: auto; box-shadow: 0 24px 60px rgba(0,0,0,.3); }
.hms-modal-title { color: var(--hms-h); font-size: 18px; font-weight: 700; margin-bottom: 18px; display: flex; align-items: center; justify-content: space-between; }
.hms-modal-close { color: var(--hms-s); cursor: pointer; font-size: 24px; line-height: 1; background: none; border: none; padding: 0 4px; }
.hms-modal-close:hover { color: var(--hms-h); }

.hms-suggest { background: var(--hms-bg4); border: 1px solid var(--hms-br); border-radius: 8px; margin-top: 6px; max-height: 200px; overflow-y: auto; }
.hms-suggest-item { padding: 8px 12px; cursor: pointer; border-bottom: 1px solid var(--hms-br); }
.hms-suggest-item:last-child { border-bottom: none; }
.hms-suggest-item:hover { background: var(--hms-primary-lt); }
.hms-picked { background: rgba(40,167,69,.08); border: 1px solid rgba(40,167,69,.3); border-radius: 8px; padding: 8px 12px; color: var(--hms-success); font-size: 13px; display: flex; align-items: center; }

.hms-alert { padding: 10px 14px; border-radius: 8px; font-size: 13px; margin-bottom: 12px; }
.hms-alert-danger { background: rgba(220,53,69,.1); color: var(--hms-danger); border: 1px solid rgba(220,53,69,.3); }

.hms-toast { position: fixed; bottom: 24px; right: 24px; background: var(--hms-bg1); border: 1px solid var(--hms-success); color: var(--hms-h); padding: 12px 18px; border-radius: 10px; box-shadow: 0 12px 32px rgba(40,167,69,.2); z-index: 1000; font-size: 13px; display: flex; align-items: center; }
.hms-toast i { color: var(--hms-success); }
.hms-toast.hms-toast-error { border-color: var(--hms-danger); box-shadow: 0 12px 32px rgba(220,53,69,.2); }
.hms-toast.hms-toast-error i { color: var(--hms-danger); }
.hms-toast-enter-active, .hms-toast-leave-active { transition: all .25s ease; }
.hms-toast-enter-from, .hms-toast-leave-to { opacity: 0; transform: translateY(20px); }
</style>

<script>
/* eslint-disable */
import Navigation from "@/components/hms-auth/Navigation.vue";
import { executeJson, hms } from "@/api";
import { getRegistryConfig } from "@/components/hms-registry/registry-config.js";

export default {
  name: "RegistryView",
  components: { Navigation },
  data() {
    return {
      cfg: null,
      rows: [],
      loading: true,
      processing: false,
      search: "",
      filterStatus: "",

      showEnroll: false,
      enrollError: "",
      enrollForm: {},

      patientSearch: "",
      patientSearchTimer: null,
      patientResults: [],

      toastMsg: "",
      toastError: false,
      toastTimer: null,

      branchId: sessionStorage.getItem("branchId")
    };
  },
  computed: {
    filtered() {
      var list = this.rows;
      if (this.filterStatus) list = list.filter(r => r.status === this.filterStatus);
      var q = this.search.trim().toLowerCase();
      if (q) {
        list = list.filter(r => {
          return JSON.stringify(r).toLowerCase().indexOf(q) >= 0;
        });
      }
      return list;
    }
  },
  watch: {
    "$route.params.type"(newType) {
      this.loadConfig(newType);
    }
  },
  mounted() {
    if (!sessionStorage.getItem("isLoggedIn")) {
      this.$router.push("/login");
      return;
    }
    this.applyTheme();
    this.loadConfig(this.$route.params.type);
  },
  beforeUnmount() {
    if (this.patientSearchTimer) clearTimeout(this.patientSearchTimer);
    if (this.toastTimer) clearTimeout(this.toastTimer);
  },
  methods: {
    applyTheme() {
      var t = localStorage.getItem("hms_theme") || document.documentElement.getAttribute("data-theme") || "light";
      document.documentElement.setAttribute("data-theme", t);
    },
    loadConfig(type) {
      this.cfg = getRegistryConfig(type);
      if (!this.cfg) { this.loading = false; return; }
      this.search = "";
      this.filterStatus = "";
      this.loadRows();
    },
    async loadRows() {
      this.loading = true;
      try {
        var r = await executeJson({ function: this.cfg.listFn, branch_id: this.branchId }, hms);
        if (r.data && r.data.success) {
          this.rows = (r.data.data && r.data.data[this.cfg.dataKey]) || [];
        }
      } catch (e) { console.error(e); }
      this.loading = false;
    },
    countByStatus(s) { return this.rows.filter(r => r.status === s).length; },
    statusClass(status) {
      var opt = this.cfg.statusOptions.find(o => o.value === status);
      return opt ? opt.class : "hms-badge-neutral";
    },
    statusLabel(status) {
      var opt = this.cfg.statusOptions.find(o => o.value === status);
      return opt ? opt.label : status;
    },
    formatDate(d) {
      if (!d) return "—";
      try { var dt = new Date(d); if (isNaN(dt.getTime())) return d; return dt.toLocaleDateString(undefined, { day:'2-digit', month:'short', year:'numeric' }); } catch(e) { return d; }
    },
    showToast(msg, isError) {
      this.toastMsg = msg; this.toastError = !!isError;
      if (this.toastTimer) clearTimeout(this.toastTimer);
      var self = this;
      this.toastTimer = setTimeout(() => { self.toastMsg = ""; }, 2400);
    },

    openEnroll() {
      this.enrollForm = { patient_id: null, patient_name: "", patient_no: "" };
      // initialize default values from cfg
      this.cfg.enrollFields.forEach(f => {
        if (f.type === 'select' && f.options && f.options.length) {
          this.enrollForm[f.name] = f.options[0].value;
        } else if (f.type === 'checkbox') {
          this.enrollForm[f.name] = 0;
        } else if (f.type === 'number') {
          this.enrollForm[f.name] = "";
        } else {
          this.enrollForm[f.name] = "";
        }
      });
      this.enrollError = "";
      this.patientSearch = "";
      this.patientResults = [];
      this.showEnroll = true;
    },
    onPatientSearch() {
      if (this.patientSearchTimer) clearTimeout(this.patientSearchTimer);
      var self = this;
      if (!this.patientSearch || this.patientSearch.length < 2) { this.patientResults = []; return; }
      this.patientSearchTimer = setTimeout(async function() {
        try {
          var r = await executeJson({ function: "searchPatients", q: self.patientSearch.trim() }, hms);
          if (r.data && r.data.success) self.patientResults = (r.data.data && r.data.data.Patients) || [];
        } catch (e) {}
      }, 300);
    },
    pickPatient(p) {
      this.enrollForm.patient_id   = p.patient_id;
      this.enrollForm.patient_name = (p.first_name + ' ' + p.last_name).trim();
      this.enrollForm.patient_no   = p.patient_no;
      this.patientResults = [];
      this.patientSearch  = "";
    },
    clearPatient() {
      this.enrollForm.patient_id = null;
      this.enrollForm.patient_name = "";
      this.enrollForm.patient_no = "";
    },
    async doEnroll() {
      this.enrollError = "";
      if (!this.enrollForm.patient_id) { this.enrollError = "Pick a patient."; return; }
      this.processing = true;
      try {
        var payload = Object.assign({ function: this.cfg.enrollFn }, this.enrollForm);
        delete payload.patient_name;
        delete payload.patient_no;
        var r = await executeJson(payload, hms);
        if (r.data && r.data.success) {
          this.showToast("Enrolled to " + this.cfg.title);
          this.showEnroll = false;
          await this.loadRows();
        } else { this.enrollError = r.data.message || "Failed."; }
      } catch (e) { this.enrollError = "Connection error."; }
      this.processing = false;
    }
  }
};
</script>
